import Title from "common/Typography/Title.tsx";
import Button from "common/Buttons/Button.tsx";
import { Telegram } from "common/Icons/Icons.tsx";
import styled, { useTheme } from "styled-components";
import banner_bg from "images/admin_community_banner.svg";
import dark_banner_bg from "images/dark_admin_community_banner.svg";
import Flex from "common/Boxes/Flex.tsx";
import { useSelector } from "react-redux";
import { selectIsDarkMode } from "@shared/slices/common.slice.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { LanguageNameSpaces } from "@shared/types";
import { Send } from "lucide-react";

const AdminCommunityBanner = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const isDarkMode = useSelector(selectIsDarkMode);
  const { isNewLook } = useTheme();
  const join = () =>
    window.open(ADMIN_COMMUNITY_LINK, "_blank", "noopener,noreferrer");

  if (isNewLook) {
    // A solid brand card: the light illustrated banner blends into the new
    // look's pale sidebar.
    return (
      <NewLookBanner>
        <PlaneTile>
          <Send size={15} strokeWidth={2} />
        </PlaneTile>
        <NewLookTitle>{t("sider.join_admin_community")}</NewLookTitle>
        <NewLookJoin type="button" onClick={join}>
          <Telegram />
          {t("sider.join_us")}
        </NewLookJoin>
      </NewLookBanner>
    );
  }

  return (
    <BannerWrapper>
      <ContentWrapper>
        <img
          alt="Join Admin Community"
          src={isDarkMode ? dark_banner_bg : banner_bg}
          style={{ width: "100%" }}
        />
        <TextsWrapper>
          <StyledTitle level={4}>{t("sider.join_admin_community")}</StyledTitle>
          <StyledButton onClick={join}>
            <TelegramIcon /> {t("sider.join_us")}
          </StyledButton>
        </TextsWrapper>
      </ContentWrapper>
    </BannerWrapper>
  );
};

export default AdminCommunityBanner;

const ADMIN_COMMUNITY_LINK = "https://t.me/+va3IVG9EVdNiZTNk";

const BannerWrapper = styled(Flex)`
  padding: 16px;
  justify-content: center;
  position: relative;
  width: 100%;
`;
const ContentWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 240px;
  display: flex;
  align-items: center;
`;
const TextsWrapper = styled.div`
  position: absolute;
  left: 16px;
  max-width: 100px;
`;
const StyledTitle = styled(Title)`
  font-weight: ${(p) => p.theme.typography.fontWeights.EXTRA_BOLD};
  text-transform: uppercase;
  margin-bottom: 12px;
  line-height: 22px;
`;
const StyledButton = styled(Button)`
  border-color: ${(p) => p.theme.colors.primaryBlue} !important;
  color: ${(p) => p.theme.colors.primaryBlue} !important;
  background: transparent !important;
  display: flex;
  align-items: center;
  padding: 4px 12px;
`;
const TelegramIcon = styled(Telegram)`
  fill: ${(p) => p.theme.colors.primaryBlue};
  margin-right: 4px;
`;

// Fixed brand colours, so the card reads the same on light and dark sidebars.
const BANNER_BLUE = "#3566D8";
const BANNER_VIOLET = "#6A4FE0";

const NewLookBanner = styled.div`
  position: relative;
  overflow: hidden;
  margin: 8px 12px 12px;
  padding: 14px;
  border-radius: 12px;
  background: linear-gradient(135deg, ${BANNER_BLUE} 0%, ${BANNER_VIOLET} 100%);
  color: #fff;
  &::before,
  &::after {
    content: "";
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
    pointer-events: none;
  }
  &::before {
    width: 120px;
    height: 120px;
    right: -44px;
    top: -52px;
  }
  &::after {
    width: 72px;
    height: 72px;
    right: 18px;
    bottom: -40px;
  }
`;
const PlaneTile = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
`;
const NewLookTitle = styled.div`
  position: relative;
  margin: 10px 0 12px;
  max-width: 180px;
  font-size: 14px;
  line-height: 20px;
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
`;
const NewLookJoin = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 14px 0 12px;
  border: none;
  border-radius: 8px;
  background: #fff;
  color: ${BANNER_BLUE};
  font-size: 13px;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  cursor: pointer;
  transition: background-color 150ms;
  svg {
    width: 14px;
    height: 14px;
    fill: currentColor;
  }
  &:hover {
    background: rgba(255, 255, 255, 0.9);
  }
  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }
`;
